import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import RiskMatrix from '../RiskMatrix';
import './AddAssetModal.css';

export interface RiskMatrixModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialLikelihood?: number;
  initialSignificance?: number;
  onConfirm: (likelihood: number, significance: number) => void;
}

/**
 * Modal that displays the risk matrix and allows the user to select
 * a cell. On confirm, calls onConfirm(likelihood, significance).
 */
const RiskMatrixModal: React.FC<RiskMatrixModalProps> = ({
  isOpen,
  onClose,
  initialLikelihood,
  initialSignificance,
  onConfirm,
}) => {
  const [likelihood, setLikelihood] = useState<number | undefined>(initialLikelihood);
  const [significance, setSignificance] = useState<number | undefined>(initialSignificance);

  useEffect(() => {
    if (isOpen) {
      setLikelihood(initialLikelihood);
      setSignificance(initialSignificance);
    }
  }, [isOpen, initialLikelihood, initialSignificance]);

  const handleConfirm = () => {
    if (likelihood !== undefined && significance !== undefined) {
      onConfirm(likelihood, significance);
      onClose();
    }
  };

  const canConfirm = likelihood !== undefined && significance !== undefined;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content risk-matrix-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Select risk level</h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm" type="button" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">
          <RiskMatrix
            likelihood={likelihood}
            significance={significance}
            onSelect={(l, s) => {
              setLikelihood(l);
              setSignificance(s);
            }}
            readonly={false}
            showLabels={true}
            size="medium"
          />
        </div>
        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn btn-ghost">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="btn btn-primary"
            disabled={!canConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default RiskMatrixModal;
